import { useState } from "react";
import { ApiError } from "@workspace/api-client-react/custom-fetch";
import { motion, AnimatePresence } from "framer-motion";
import { TranscriptionProgress } from "@/components/transcription-progress";
import {
  AlertTriangle,
  Mic,
  Square,
  Upload,
  MicOff,
  ShieldAlert,
  Settings,
  Clock,
  ChevronDown,
} from "lucide-react";
import type { UseAudioRecorderReturn } from "@/hooks/use-audio-recorder";

function getApiErrorDetails(err: { message?: string } | null | undefined): string | undefined {
  if (err instanceof ApiError && err.data !== null && typeof err.data === "object") {
    const details = (err.data as Record<string, unknown>).details;
    if (typeof details === "string" && details.trim()) return details;
  }
  return undefined;
}

interface TrainAudioMutation {
  isPending: boolean;
  isError: boolean;
  error?: { message?: string } | null;
  reset: () => void;
}

interface MicRecordingPanelProps {
  recorder: UseAudioRecorderReturn;
  trainAudioMutation: TrainAudioMutation;
  onFileUpload: (e: React.ChangeEvent<HTMLInputElement>) => void;
  fileInputRef: React.RefObject<HTMLInputElement | null>;
  onCancelUpload?: () => void;
}

const SHORT_RECORDING_HINT_SECONDS = 10;

function stripHttpPrefix(message: string | undefined): string | undefined {
  if (!message) return message;
  return message.replace(/^HTTP \d{3}[^:]*:\s*/, "");
}

function MicArea({
  recorder,
  fileInputRef,
}: {
  recorder: UseAudioRecorderReturn;
  fileInputRef: React.RefObject<HTMLInputElement | null>;
}) {
  if (recorder.micPermission === "unavailable") {
    return (
      <div className="flex flex-col items-center gap-4 py-4 text-center">
        <div className="w-16 h-16 rounded-full bg-destructive/10 flex items-center justify-center">
          <MicOff className="w-7 h-7 text-destructive" />
        </div>
        <div>
          <h3 className="text-lg font-semibold text-foreground">Microphone Not Available</h3>
          <p className="text-sm text-muted-foreground mt-1">
            Voice recording is not supported in this browser. Please try Chrome or Safari 14.3+.
            You can still upload audio files below.
          </p>
        </div>
        <button
          onClick={() => fileInputRef.current?.click()}
          className="px-4 py-2 rounded-lg bg-secondary text-foreground text-sm font-medium hover:bg-secondary/80 transition-colors flex items-center gap-2"
        >
          <Upload className="w-4 h-4" /> Upload audio instead
        </button>
      </div>
    );
  }

  if (recorder.micPermission === "denied") {
    return (
      <div className="flex flex-col items-center gap-4 py-2 text-center">
        <div className="w-14 h-14 rounded-full bg-amber-500/10 flex items-center justify-center">
          <ShieldAlert className="w-6 h-6 text-amber-400" />
        </div>
        <div>
          <h3 className="text-base font-semibold text-foreground">Microphone Blocked</h3>
          <p className="text-sm text-muted-foreground mt-1 max-w-xs">
            Tap the <Settings className="w-3.5 h-3.5 inline" /> lock icon in your address bar, allow Microphone, then tap Try Again.
          </p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={recorder.recheckPermission}
            className="px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 transition-colors"
          >
            Try again
          </button>
          <button
            onClick={() => fileInputRef.current?.click()}
            className="px-4 py-2 rounded-lg bg-secondary text-foreground text-sm font-medium hover:bg-secondary/80 transition-colors flex items-center gap-2"
          >
            <Upload className="w-4 h-4" /> Upload instead
          </button>
        </div>
      </div>
    );
  }

  if (recorder.deviceError) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="flex flex-col items-center gap-4 py-2 text-center"
      >
        <div className="w-14 h-14 rounded-full bg-amber-500/10 flex items-center justify-center">
          <AlertTriangle className="w-6 h-6 text-amber-400" />
        </div>
        <p className="text-sm font-medium text-foreground">{recorder.deviceError}</p>
        <div className="flex gap-3">
          <button
            onClick={() => {
              recorder.clearDeviceError();
              recorder.startRecording();
            }}
            className="px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 transition-colors"
          >
            Try again
          </button>
          <button
            onClick={() => {
              recorder.clearDeviceError();
              fileInputRef.current?.click();
            }}
            className="px-4 py-2 rounded-lg bg-secondary text-foreground text-sm font-medium hover:bg-secondary/80 transition-colors flex items-center gap-2"
          >
            <Upload className="w-4 h-4" /> Upload instead
          </button>
        </div>
      </motion.div>
    );
  }

  const isShortRecording = recorder.isRecording && recorder.recordingTime < SHORT_RECORDING_HINT_SECONDS;

  return (
    <div className="flex flex-col items-center relative w-full">
      {recorder.isNearLimit && (
        <motion.div
          initial={{ opacity: 0, y: -4 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-4 flex items-center gap-2 px-3 py-1.5 rounded-lg bg-amber-500/10 border border-amber-500/30 text-amber-400 text-xs font-medium"
        >
          <Clock className="w-3.5 h-3.5 flex-shrink-0" />
          Recording will auto-stop at {Math.floor(recorder.maxDurationSeconds / 60)} min — wrap up soon
        </motion.div>
      )}

      <div className="flex gap-4 items-center">
        <div className="relative">
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={recorder.isRecording ? recorder.stopRecording : recorder.startRecording}
            className={`w-20 h-20 rounded-full flex items-center justify-center shadow-xl transition-colors duration-300 relative ${
              recorder.isRecording
                ? "bg-destructive text-destructive-foreground shadow-destructive/30"
                : "bg-primary text-primary-foreground shadow-primary/30 hover:bg-primary/90"
            }`}
          >
            {recorder.isRecording ? <Square className="w-8 h-8 fill-current" /> : <Mic className="w-8 h-8" />}
            {recorder.isRecording && (
              <>
                <span className="absolute w-full h-full rounded-full border-2 border-destructive animate-ping opacity-75" style={{ animationDuration: "1.5s" }} />
                <span className="absolute w-full h-full rounded-full border-2 border-destructive animate-ping opacity-50" style={{ animationDuration: "2s" }} />
              </>
            )}
          </motion.button>
        </div>

        <div className="w-px h-16 bg-border" />

        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={() => fileInputRef.current?.click()}
          disabled={recorder.isRecording}
          className="w-20 h-20 rounded-full flex items-center justify-center bg-secondary text-foreground shadow-md hover:bg-secondary/80 transition-colors disabled:opacity-40 disabled:pointer-events-none"
        >
          <Upload className="w-7 h-7" />
        </motion.button>
      </div>

      <div className="mt-6 text-center">
        {recorder.isRecording ? (
          <motion.div initial={{ opacity: 0, y: 5 }} animate={{ opacity: 1, y: 0 }} className="flex flex-col items-center gap-2">
            <div className="flex items-center gap-3">
              <div className="w-2 h-2 rounded-full bg-destructive animate-pulse" />
              <span className="text-2xl font-display font-medium tabular-nums tracking-tight">{recorder.formatTime(recorder.recordingTime)}</span>
            </div>
            <AnimatePresence mode="wait">
              {isShortRecording ? (
                <motion.p
                  key="short-hint"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="text-xs text-muted-foreground"
                >
                  Sweet spot is 10+ seconds for best results
                </motion.p>
              ) : null}
            </AnimatePresence>
          </motion.div>
        ) : (
          <div>
            <h3 className="text-lg font-semibold text-foreground">Record, Upload Audio or Video</h3>
            <p className="text-sm text-muted-foreground mt-1">Talk naturally or upload a voice memo, podcast clip, or video. The AI extracts your cadence and vocabulary.</p>
          </div>
        )}
      </div>
    </div>
  );
}

export function MicRecordingPanel({ recorder, trainAudioMutation, onFileUpload, fileInputRef, onCancelUpload }: MicRecordingPanelProps) {
  const err = trainAudioMutation.error;
  const rawErrorMessage = err?.message;
  const errorMessage = stripHttpPrefix(rawErrorMessage);
  const detailText = getApiErrorDetails(err) ?? (rawErrorMessage && rawErrorMessage.length > 60 ? rawErrorMessage : undefined);
  const [showErrorDetail, setShowErrorDetail] = useState(false);

  const capturedDuration = !recorder.isRecording && recorder.recordingTime > 0
    ? recorder.formatTime(recorder.recordingTime)
    : null;

  return (
    <div className="space-y-6">
      <div className="flex flex-col items-center justify-center p-8 border-2 border-dashed border-border rounded-2xl bg-card/30 relative overflow-hidden">
        {trainAudioMutation.isPending ? (
          <TranscriptionProgress
            isActive
            variant="transcription"
            capturedDuration={capturedDuration ? `Captured ${capturedDuration}` : undefined}
            onCancel={onCancelUpload}
          />
        ) : trainAudioMutation.isError ? (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="flex flex-col items-center gap-4 py-4 text-center"
          >
            <div className="w-16 h-16 rounded-full bg-destructive/10 flex items-center justify-center">
              <AlertTriangle className="w-7 h-7 text-destructive" />
            </div>
            <div className="max-w-xs">
              <h3 className="text-base font-semibold text-foreground">Audio Training Failed</h3>
              <p className="text-sm text-muted-foreground mt-1">
                {errorMessage || "We couldn't process that audio. The file may be too short, corrupted, or in an unsupported format."}
              </p>
              {detailText && (
                <button
                  onClick={() => setShowErrorDetail((v) => !v)}
                  className="flex items-center gap-1 mt-2 mx-auto text-xs text-muted-foreground/70 hover:text-muted-foreground transition-colors"
                >
                  <ChevronDown className={`w-3 h-3 transition-transform ${showErrorDetail ? "rotate-180" : ""}`} />
                  {showErrorDetail ? "Hide details" : "What went wrong?"}
                </button>
              )}
              {showErrorDetail && detailText && (
                <p className="mt-2 text-xs text-muted-foreground/60 bg-muted/30 rounded-lg px-3 py-2 break-words text-left">
                  {detailText}
                </p>
              )}
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => { trainAudioMutation.reset(); setShowErrorDetail(false); }}
                className="px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 transition-colors"
              >
                Record again
              </button>
              <button
                onClick={() => {
                  trainAudioMutation.reset();
                  setShowErrorDetail(false);
                  fileInputRef.current?.click();
                }}
                className="px-4 py-2 rounded-lg bg-secondary text-foreground text-sm font-medium hover:bg-secondary/80 transition-colors flex items-center gap-2"
              >
                <Upload className="w-4 h-4" /> Upload different file
              </button>
            </div>
          </motion.div>
        ) : (
          <MicArea recorder={recorder} fileInputRef={fileInputRef} />
        )}
      </div>

      <input ref={fileInputRef} type="file" accept="audio/*,video/*" className="hidden" onChange={onFileUpload} />

      <div className="flex items-center justify-center gap-2 py-2 px-4 bg-emerald-950/40 border border-emerald-800/40 rounded-xl text-xs text-emerald-400">
        <svg viewBox="0 0 24 24" className="w-3.5 h-3.5 fill-none stroke-current stroke-2 flex-shrink-0">
          <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z" />
        </svg>
        Your audio and video files are transcribed then immediately discarded. Only the text is kept.
      </div>
    </div>
  );
}
